import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import mediaFileObject from '@/entities/mediaFile/interface'

import {
  ALL_IMAGE_GALLERIES_QUERY,
  SINGLE_IMAGE_GALLERY_QUERY,
  IMAGE_GALLERIES_DEPENDENTS_QUERY,
  CREATE_IMAGE_GALLERY_MUTATION,
  UPDATE_IMAGE_GALLERY_MUTATION,
  DELETE_IMAGE_GALLERY_MUTATION,
  FORCE_DELETE_IMAGE_GALLERY_MUTATION,
  ADD_IMAGE_GALLERY_IMAGES_MUTATION,
  REMOVE_IMAGE_GALLERY_IMAGES_MUTATION,
  CREATE_IMAGE_GALLERY_IMAGES_MUTATION,
  DELETE_IMAGE_GALLERY_IMAGES_MUTATION,
  UPDATE_IMAGE_GALLERY_IMAGES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const imageGalleryRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_IMAGE_GALLERY_QUERY, variables).then((data: any) => {
      return hydrateObject(data.imageGallery)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_IMAGE_GALLERIES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.imageGalleries)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_IMAGE_GALLERY_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.imageGallery),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_IMAGE_GALLERIES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.imageGalleries),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(IMAGE_GALLERIES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([IMAGE_GALLERIES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_IMAGE_GALLERY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.imageGallery)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_IMAGE_GALLERY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.imageGallery)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_IMAGE_GALLERY_MUTATION, variables).then((data: any) => {
      return data.deleteImageGallery
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_IMAGE_GALLERY_MUTATION, variables).then((data: any) => {
      return data.forceDeleteImageGallery
    })
  },

  assignImages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        imageIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_IMAGE_GALLERY_IMAGES_MUTATION, variables)
  },

  unassignImages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        imageIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_IMAGE_GALLERY_IMAGES_MUTATION, variables)
  },

  createImages: async (id: string, items: Array<mediaFileObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        images: items,
      },
    }
    return graphQLClient.request(CREATE_IMAGE_GALLERY_IMAGES_MUTATION, variables)
  },

  deleteImages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        imageIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_IMAGE_GALLERY_IMAGES_MUTATION, variables)
  },

  updateImagesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        imageIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_IMAGE_GALLERY_IMAGES_ORDER_MUTATION, variables)
  },
}

export default imageGalleryRepository
